import { Group, SegmentedControl, Select, Stack, Text, TextInput } from "@mantine/core";
import { keepPreviousData } from "@tanstack/react-query";
import { useState } from "react";

import { useListStaffUsage } from "../../api/generated/endpoints/staff/staff";
import type { UsageGroup, UsageNames } from "../../api/generated/model";
import { toUserMessage } from "../../platform/messages";
import { keyLabel } from "./usage-format";
import {
  DEFAULT_DAYS,
  type FilterGroup,
  GROUPS,
  type UsageSearch,
  daysBefore,
  filtersOf,
  narrowed,
  rangeOf,
  today,
  without,
} from "./usage-search";

const PRESETS = [7, 30, 90, 365];
const CUSTOM = "custom";
const STARTED = [
  ...PRESETS.map((days) => ({ value: String(days), label: `Last ${days} days` })),
  { value: CUSTOM, label: "Custom range" },
];

const FILTERS: { by: FilterGroup; label: string; all: string }[] = [
  { by: "team", label: "Team", all: "All teams" },
  { by: "brand", label: "Brand", all: "All brands" },
  { by: "user", label: "User", all: "All users" },
  { by: "model", label: "Model", all: "All models" },
  { by: "type", label: "Type", all: "All types" },
];

export interface UsageFiltersProps {
  search: UsageSearch;
  onChange: (next: UsageSearch) => void;
}

/**
 * Usage's filters, each in the URL (spec usage-analytics 3): the UTC days a generation started,
 * its team, brand, user, model and type, and the grouping. They scope everything below them.
 */
export function UsageFilters({ search, onChange }: UsageFiltersProps) {
  const { from, to } = rangeOf(search);
  const now = today();
  const preset = PRESETS.find((days) => to === now && from === daysBefore(now, days - 1));
  const pick = (value: string | null) => {
    if (value === null || value === CUSTOM) return;
    const days = Number(value);
    // The default stays out of the URL, so a link to it keeps meaning the last 30 days.
    const range = days === DEFAULT_DAYS ? {} : { from: daysBefore(now, days - 1), to: now };
    onChange({ ...search, from: undefined, to: undefined, ...range });
  };
  // A date typed in pins both ends, so the other one stays where it was shown.
  const dates = (patch: UsageSearch) => onChange({ ...search, from, to, ...patch });
  return (
    <Stack gap="sm">
      <Group gap="sm" align="flex-end">
        <Select
          label="Started"
          data={STARTED}
          value={preset === undefined ? CUSTOM : String(preset)}
          allowDeselect={false}
          onChange={pick}
          w={160}
        />
        <TextInput
          type="date"
          label="From"
          value={from}
          max={to}
          onChange={(event) => dates({ from: event.currentTarget.value || undefined })}
          w={160}
        />
        <TextInput
          type="date"
          label="To"
          value={to}
          min={from}
          max={now}
          onChange={(event) => dates({ to: event.currentTarget.value || undefined })}
          w={160}
        />
        {FILTERS.map((filter) => (
          <FilterSelect key={filter.by} {...filter} search={search} onChange={onChange} />
        ))}
      </Group>
      <Group gap="xs">
        <Text size="sm" c="var(--cai-color-text-muted)">
          Group by
        </Text>
        <SegmentedControl
          aria-label="Group by"
          data={GROUPS}
          value={search.by ?? "user"}
          onChange={(by) => onChange({ ...search, by: by === "user" ? undefined : (by as UsageGroup) })}
        />
      </Group>
    </Stack>
  );
}

/** A choice's words: a user's name beside their email, a type's beside its id, else the key's. */
function choiceLabel(by: FilterGroup, key: string, names: UsageNames): string {
  const user = by === "user" ? names.users[key] : undefined;
  if (user !== undefined && user.name !== null) return `${user.name} (${user.email})`;
  const label = keyLabel(by, key, names);
  return by === "type" && label !== key ? `${label} (${key})` : label;
}

/**
 * One filter. Its choices are its own grouping's keys that the other filters leave, with their
 * names, read when it first opens or holds a value to name. A row with no key (no user, brand or
 * model) is no choice: nothing filters by it.
 */
function FilterSelect(props: { by: FilterGroup; label: string; all: string } & UsageFiltersProps) {
  const { by, label, all, search, onChange } = props;
  const value = search[by];
  const [opened, setOpened] = useState(false);
  const choices = useListStaffUsage(
    { ...filtersOf(without(search, by)), group_by: by },
    { query: { enabled: opened || value !== undefined, placeholderData: keepPreviousData } },
  );
  const page = choices.data;
  const data =
    page === undefined
      ? []
      : page.rows.flatMap((row) =>
          row.key === null ? [] : [{ value: row.key, label: choiceLabel(by, row.key, page.names) }],
        );
  if (value !== undefined && !data.some((choice) => choice.value === value)) {
    data.unshift({ value, label: value });
  }
  return (
    <Select
      label={label}
      placeholder={all}
      data={data}
      value={value ?? null}
      searchable
      clearable
      onDropdownOpen={() => setOpened(true)}
      onChange={(next) => onChange(next === null ? without(search, by) : narrowed(search, by, next))}
      nothingFoundMessage={choices.isFetching ? "Loading…" : "None in these days"}
      // The API answers at most 500 groups, so a search here finds only among those.
      description={page?.more ? "The first 500 only" : undefined}
      error={choices.isError ? toUserMessage(choices.error) : undefined}
      w={200}
    />
  );
}
